/**
 * Squarified treemap: worst-aspect-ratio row packing into absolute rects
 * (plan §3.3 · §4.5 — "the most bug-prone pure function in the project").
 *
 * The greedy loop is imperative because that is what the algorithm is: each pass
 * consumes a prefix of the remaining items into one row, then shrinks the free
 * box. Every mutation is confined to locals inside this function — the caller's
 * array is copied before sorting, and the emitted rects are new objects.
 *
 * Two deliberate departures from demos/Meridian Terminal.dc.html line 755:
 *
 *  1. **Non-positive values are dropped up front.** The demo's greedy step
 *     computes an aspect ratio of `Infinity` for a zero-area tile, breaks the
 *     row, and then abandons the item when the free box runs out — so it drops
 *     it too, just further downstream and only sometimes. Dropping it here is
 *     the same outcome, stated once. Emitting a zero-area rect instead would be
 *     worse than either: plan §4.6 requires every tile to be focusable and
 *     named, and a focusable tile with no area is a keyboard trap with nothing
 *     to show.
 *
 *  2. **No absolute minimum-strip guard.** The demo stops while `w > 0.01`,
 *     which silently abandons any items still queued — on a 100×100 box that is
 *     invisible, but plan §4.5 requires `Σ(w·h) === W·H` within 1e-6 and the
 *     guard breaks it outright for small boxes. Termination does not depend on
 *     it: a row always takes at least one item, so `list` strictly shrinks.
 *     The final row instead SNAPS to the remaining dimension, which is
 *     algebraically what `sum / side` already computes and removes the
 *     floating-point residue that could otherwise leave a negative-width strip.
 *
 * Row area is `Σ(length · cross) = Σ(v · scale) = sum` for ANY `length`, so
 * exact tiling follows from dropping no positive item — not from the geometry.
 */
import { worstAspectRatio } from './aspect-ratio';

export interface SquarifyItem {
  /** Non-negative magnitude — market cap on the dashboard treemap. */
  readonly v: number;
}

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/* eslint-disable functional/no-let, functional/no-loop-statements, functional/immutable-data */

/** Greedy prefix of `list` that does not worsen the row's worst aspect ratio. */
function takeRow<T extends SquarifyItem>(
  list: readonly T[],
  scale: number,
  side: number,
): { readonly row: readonly T[]; readonly sum: number } {
  let row: T[] = [];
  let sum = 0;
  let best = Number.POSITIVE_INFINITY;
  /* O(k²) allocations per row: each candidate rebuilds the row array and remaps
     areas. Rows stay short (the aspect heuristic breaks them early) and n≈560,
     so the cost is bounded; do not "optimise" without a profile — readability
     matches the published algorithm. An incremental min/max/sum is possible. */
  for (const item of list) {
    const nextSum = sum + item.v * scale;
    const candidate = [...row, item];
    const worst = worstAspectRatio(
      candidate.map((c) => c.v * scale),
      nextSum / side,
    );
    if (worst <= best || row.length === 0) {
      row = candidate;
      sum = nextSum;
      best = worst;
    } else break;
  }
  return { row, sum };
}

interface RowPlacement<T> {
  readonly row: readonly T[];
  readonly scale: number;
  /** Extent along the packing axis, shared by every tile in the row. */
  readonly length: number;
  readonly horizontal: boolean;
  readonly x: number;
  readonly y: number;
}

/** Lay one committed row into absolute rects, advancing along the cross axis. */
function placeRow<T extends SquarifyItem>(p: RowPlacement<T>): readonly (T & Rect)[] {
  let offset = p.horizontal ? p.y : p.x;
  const placed: (T & Rect)[] = [];
  for (const item of p.row) {
    const cross = (item.v * p.scale) / p.length;
    placed.push({
      ...item,
      ...(p.horizontal
        ? { x: p.x, y: offset, w: p.length, h: cross }
        : { x: offset, y: p.y, w: cross, h: p.length }),
    });
    offset += cross;
  }
  return placed;
}

export function squarify<T extends SquarifyItem>(
  items: readonly T[],
  width: number,
  height: number,
): readonly (T & Rect)[] {
  const positive = items.filter((i) => i.v > 0);
  const total = positive.reduce((a, i) => a + i.v, 0);
  if (positive.length === 0 || width <= 0 || height <= 0 || total <= 0) return [];

  const out: (T & Rect)[] = [];
  let list = [...positive].sort((a, b) => b.v - a.v);
  const scale = (width * height) / total;
  let x = 0;
  let y = 0;
  let w = width;
  let h = height;

  while (list.length > 0) {
    const horizontal = w >= h;
    const side = horizontal ? h : w;
    /* INVARIANT: `side > 0` whenever this body runs. The box starts positive on
       both axes; a non-final row consumes strictly less than the remaining area,
       so the shrinking axis stays positive, and the final row snaps to exactly
       the remaining dimension and empties `list`. Stated rather than guarded:
       a defensive branch here would be permanently unexecuted code sitting in
       the middle of the one function in the package that most needs its
       coverage to mean something. The property suite exercises it across every
       generated layout in layout.test.ts (~300 runs: 100 × three properties). */
    const { row, sum } = takeRow(list, scale, side);
    const isFinalRow = row.length === list.length;
    const length = isFinalRow ? (horizontal ? w : h) : sum / side;
    out.push(...placeRow({ row, scale, length, horizontal, x, y }));
    if (horizontal) {
      x += length;
      w -= length;
    } else {
      y += length;
      h -= length;
    }
    list = list.slice(row.length);
  }
  return out;
}

/* eslint-enable functional/no-let, functional/no-loop-statements, functional/immutable-data */
